import { FormEvent, PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeElementLocale, StripeElementsOptionsMode, loadStripe } from "@stripe/stripe-js";
import { useAlert } from "capitalrx-components-2.0/lib/general";

import { initiateSetupIntent } from "@/App/api/medicarePrescriptionPaymentPlan";
import { AuthenticationContext, MemberEligibilityContext } from "@/App/context";
import { PaymentMethod } from "@/App/types/medicarePrescriptionPaymentPlan";

import { usePlanYear } from "../PlanYearContext";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.stripeIntentForm";

type StripeIntentFormProps = {
  paymentDay: number;
  paymentMethod: PaymentMethod;
  submitRef: React.LegacyRef<HTMLButtonElement>;
  handleSavePaymentDetails: (day: number, method: string, customerId?: string) => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
  handleLoadingError: () => void;
};

export const StripeIntentForm = ({
  setIsLoading,
  paymentDay,
  paymentMethod,
  submitRef,
  handleSavePaymentDetails,
  handleLoadingError,
}: StripeIntentFormProps) => {
  const { t: translate } = useTranslation();

  const stripe = useStripe();
  const elements = useElements();
  const showAlert = useAlert();
  const { getAuthToken } = useContext(AuthenticationContext);
  const planYear = usePlanYear();

  const url = window.location.href;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleErrorMessage = (err: any) => {
    showAlert({
      message: typeof err === "string" ? err : err?.data?.message || err.message,
      type: "error",
      style: "banner",
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleErrorMessage(submitError);
      setIsLoading(false);
      return;
    }

    try {
      const { client_secret, customer_id } = await initiateSetupIntent(await getAuthToken(), {
        preferred_payment_method: paymentMethod,
        plan_year: planYear,
      });

      const setupIntentResposne = await stripe.confirmSetup({
        elements,
        clientSecret: client_secret,
        redirect: "if_required",
        confirmParams: {
          return_url: url,
        },
      });

      if (setupIntentResposne.error) {
        handleErrorMessage(translate(`${TRANSLATION_PATH}.unableToStorePaymentInformation`));
        return;
      }

      await handleSavePaymentDetails(paymentDay, paymentMethod, customer_id);
    } catch (err) {
      handleErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (elements) {
      const element = elements.getElement("payment");
      element.on("ready", () => {
        setIsLoading(false);
      });
    }
  });

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onLoadError={handleLoadingError} />
      <button ref={submitRef} type="submit" disabled={!stripe} style={{ display: "none" }}>
        {translate(`${TRANSLATION_PATH}.submitPaymentIntent`)}
      </button>
    </form>
  );
};

export const StripeSetupElement = ({
  children,
  paymentMethod,
  onError,
}: PropsWithChildren & { readonly onError: () => void; readonly paymentMethod: string }) => {
  const { i18n } = useTranslation();

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const stripePromise = useMemo(async () => {
    if (site?.stripe?.pubKey) {
      try {
        return loadStripe(site.stripe.pubKey, {
          stripeAccount: memberEligibility.plan.payment_vendor_account_id,
        });
      } catch {
        onError();
        return Promise.reject();
      }
    } else {
      onError();
      return Promise.reject();
    }
  }, []);

  const options: StripeElementsOptionsMode = {
    locale: (i18n?.language as StripeElementLocale) || "auto",
    mode: "setup",
    paymentMethodTypes: [paymentMethod === "ach" ? "us_bank_account" : paymentMethod],
    currency: "usd",
    appearance: {
      theme: "flat",
      rules: {
        ".Input": {
          borderColor: "#AEBFCC",
          borderStyle: "solid",
          borderWidth: "1px",
          backgroundColor: "#ffffff",
          fontSize: "14px",
          padding: "10px",
        },
        ".Label": {
          fontSize: "12px",
          fontWeight: "600",
          marginBottom: "4px",
          color: "#313E48",
        },
      },
      variables: {
        colorPrimary: "#0570de",
        colorBackground: "#ffffff",
        fontFamily: "Source Sans 3 , sans-serif",
        spacingGridColumn: "12px",
        spacingGridRow: "12px",
        spacingUnit: "2px",
        borderRadius: "4px",
      },
    },
  };

  return (
    <>
      {site?.stripe?.pubKey && (
        <Elements options={options} stripe={stripePromise}>
          {children}
        </Elements>
      )}
    </>
  );
};
